import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { ReviewProgressItem } from "@compliance-sage/contracts";
import { api } from "../api/client";
import { useLessonDraftJob } from "../features/educateSage/lessonDraftJob";
import { useI18n } from "../i18n";
import { ACTIVE_JOB_STATUSES, ReviewProgressDetail } from "./ReviewProgressDetail";
import { ReviewProgressDrawer } from "./ReviewProgressDrawer";
import { PendingReviewProgress } from "./PendingReviewProgress";
import { LessonDraftingProgress } from "./LessonDraftingProgress";
import { showToast } from "./Toast";

const STORAGE_KEY = "cs-review-progress-panel:v2";
const DISMISSED_ITEMS_STORAGE_KEY = "cs-dismissed-review-progress-items";
const DEFAULT_PANEL_WIDTH = 440;
const DEFAULT_PANEL_HEIGHT = 480;
export const REVIEW_PROGRESS_EVENT = "compliance-sage:review-progress";
export const OPEN_REVIEW_PROGRESS_EVENT = "compliance-sage:open-review-progress";
export const REVIEW_PROGRESS_HAS_ITEMS_EVENT = "compliance-sage:review-progress-has-items";
// Fired right before the create-review request is sent, so the panel can show
// an "uploading" placeholder instead of waiting for the upload to finish and
// the first poll to come back.
export const REVIEW_SUBMIT_STARTED_EVENT = "compliance-sage:review-submit-started";
export const REVIEW_SUBMIT_ENDED_EVENT = "compliance-sage:review-submit-ended";
// Fired repeatedly with the upload's bytes-sent percentage while a submission
// is in flight, so the pending placeholder can show real progress instead of
// a static "uploading" sentence for the whole (potentially long) upload.
export const REVIEW_SUBMIT_PROGRESS_EVENT = "compliance-sage:review-submit-progress";

interface PendingSubmission {
  tempId: string;
  title: string;
  startedAt: number;
  uploadPercent?: number;
}

interface PanelState {
  minimized?: boolean;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
}

function readPanelState(): PanelState {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}") as PanelState;
  } catch {
    return {};
  }
}

function readDismissedItems(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_ITEMS_STORAGE_KEY) ?? "{}") as Record<string, string>;
  } catch {
    return {};
  }
}

function isDismissed(item: ReviewProgressItem, dismissedItems: Record<string, string>) {
  return dismissedItems[item.reviewId] === item.updatedAt;
}

export function ReviewProgressPanel() {
  const { t } = useI18n();
  const panelRef = useRef<HTMLDivElement>(null);
  const initialState = useMemo(readPanelState, []);
  const initialDismissedItems = useMemo(readDismissedItems, []);
  const lessonJob = useLessonDraftJob();
  const [items, setItems] = useState<ReviewProgressItem[]>([]);
  const [pending, setPending] = useState<PendingSubmission | null>(null);
  const [dismissedItems, setDismissedItems] = useState(initialDismissedItems);
  const [minimized, setMinimized] = useState(initialState.minimized ?? false);
  const [expanded, setExpanded] = useState(false);
  const [position, setPosition] = useState(
    initialState.left !== undefined && initialState.top !== undefined
      ? { left: initialState.left, top: initialState.top }
      : null,
  );
  const [size, setSize] = useState({
    width: initialState.width ?? DEFAULT_PANEL_WIDTH,
    height: initialState.height ?? DEFAULT_PANEL_HEIGHT,
  });
  // Tracks each review's last-seen job status so a transition out of an
  // active status (finished, however it finished) can raise a toast even
  // when the user has navigated away from this panel entirely.
  const previousStatusesRef = useRef<Map<string, ReviewProgressItem["jobStatus"]>>(new Map());
  const hasPolledOnceRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      if (timer) window.clearTimeout(timer);
      try {
        const next = await api.reviewProgress();
        if (cancelled) return;
        if (hasPolledOnceRef.current) {
          for (const item of next) {
            const previousStatus = previousStatusesRef.current.get(item.reviewId);
            if (
              previousStatus &&
              ACTIVE_JOB_STATUSES.has(previousStatus) &&
              !ACTIVE_JOB_STATUSES.has(item.jobStatus)
            ) {
              showToast({
                tone: item.jobStatus === "FAILED" ? "error" : "success",
                message: t(
                  item.jobStatus === "FAILED" ? "analysisToastFailed" : "analysisToastCompleted",
                  { title: item.title },
                ),
                href: `/reviews/${item.reviewId}`,
                actionLabel: t("analysisToastView"),
              });
            }
          }
        }
        hasPolledOnceRef.current = true;
        previousStatusesRef.current = new Map(next.map((item) => [item.reviewId, item.jobStatus]));
        setItems(next.filter((item) => !isDismissed(item, dismissedItems)));
        const hasActive = next.some((item) => ACTIVE_JOB_STATUSES.has(item.jobStatus));
        timer = window.setTimeout(poll, hasActive ? 3_000 : 15_000);
      } catch {
        if (!cancelled) timer = window.setTimeout(poll, 15_000);
      }
    };

    const refreshNow = () => void poll();
    window.addEventListener(REVIEW_PROGRESS_EVENT, refreshNow);
    void poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      window.removeEventListener(REVIEW_PROGRESS_EVENT, refreshNow);
    };
  }, [dismissedItems, t]);

  useEffect(() => {
    window.dispatchEvent(
      Object.assign(new Event(REVIEW_PROGRESS_HAS_ITEMS_EVENT), {
        hasItems: items.length > 0 || pending !== null || lessonJob.status !== "idle",
        activeCount:
          items.filter((item) => ACTIVE_JOB_STATUSES.has(item.jobStatus)).length +
          (pending ? 1 : 0) +
          (lessonJob.status === "drafting" ? 1 : 0),
      }),
    );
  }, [items.length, pending, lessonJob.status]);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ minimized, ...position, ...size } satisfies PanelState),
    );
  }, [minimized, position, size]);

  useEffect(() => {
    localStorage.setItem(DISMISSED_ITEMS_STORAGE_KEY, JSON.stringify(dismissedItems));
  }, [dismissedItems]);

  useEffect(() => {
    const openPanel = () => setMinimized(false);
    window.addEventListener(OPEN_REVIEW_PROGRESS_EVENT, openPanel);
    return () => window.removeEventListener(OPEN_REVIEW_PROGRESS_EVENT, openPanel);
  }, []);

  const previousLessonStatusRef = useRef(lessonJob.status);
  useEffect(() => {
    const previous = previousLessonStatusRef.current;
    previousLessonStatusRef.current = lessonJob.status;
    if (lessonJob.status === "drafting" && previous !== "drafting") {
      setMinimized(false);
    }
    if (previous !== "drafting") return;
    if (lessonJob.status !== "ready" && lessonJob.status !== "error") return;
    if (window.location.pathname.startsWith("/educate-sage/new")) return;
    showToast({
      tone: lessonJob.status === "error" ? "error" : "success",
      message: t(lessonJob.status === "error" ? "lessonDraftToastFailed" : "lessonDraftToastReady"),
      href: "/educate-sage/new",
      actionLabel: t("lessonDraftToastView"),
    });
    setMinimized(false);
  }, [lessonJob.status, t]);

  useEffect(() => {
    const onSubmitStarted = (event: Event) => {
      const detail = (event as CustomEvent<{ tempId: string; title: string }>).detail;
      if (!detail) return;
      setPending({ ...detail, startedAt: Date.now() });
      setMinimized(false);
    };
    const onSubmitProgress = (event: Event) => {
      const detail = (event as CustomEvent<{ tempId: string; percent: number }>).detail;
      if (!detail) return;
      setPending((current) =>
        current && current.tempId === detail.tempId ? { ...current, uploadPercent: detail.percent } : current,
      );
    };
    const onSubmitEnded = (event: Event) => {
      const detail = (event as CustomEvent<{ tempId: string }>).detail;
      setPending((current) => (current && current.tempId === detail?.tempId ? null : current));
    };
    window.addEventListener(REVIEW_SUBMIT_STARTED_EVENT, onSubmitStarted);
    window.addEventListener(REVIEW_SUBMIT_PROGRESS_EVENT, onSubmitProgress);
    window.addEventListener(REVIEW_SUBMIT_ENDED_EVENT, onSubmitEnded);
    return () => {
      window.removeEventListener(REVIEW_SUBMIT_STARTED_EVENT, onSubmitStarted);
      window.removeEventListener(REVIEW_SUBMIT_PROGRESS_EVENT, onSubmitProgress);
      window.removeEventListener(REVIEW_SUBMIT_ENDED_EVENT, onSubmitEnded);
    };
  }, []);

  useEffect(() => {
    const element = panelRef.current;
    if (!element || minimized) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setSize({
        width: Math.round(entry.contentRect.width),
        height: Math.round(entry.contentRect.height),
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [minimized]);

  const reviewActiveCount = items.filter((item) => ACTIVE_JOB_STATUSES.has(item.jobStatus)).length;
  const lessonActive = lessonJob.status === "drafting";
  const lessonVisible = lessonJob.status !== "idle";
  const activeCount = reviewActiveCount + (pending ? 1 : 0) + (lessonActive ? 1 : 0);
  const completedCount = items.length - reviewActiveCount;
  const totalCount = items.length + (pending ? 1 : 0) + (lessonVisible ? 1 : 0);
  if (totalCount === 0) return null;

  const clearCompleted = () => {
    setDismissedItems((previous) => ({
      ...previous,
      ...Object.fromEntries(
        items
          .filter((item) => !ACTIVE_JOB_STATUSES.has(item.jobStatus))
          .map((item) => [item.reviewId, item.updatedAt]),
      ),
    }));
    setItems((previous) => previous.filter((item) => ACTIVE_JOB_STATUSES.has(item.jobStatus)));
  };

  const beginDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button, a")) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    event.currentTarget.setPointerCapture(event.pointerId);

    const move = (moveEvent: PointerEvent) => {
      setPosition({
        left: Math.max(8, Math.min(window.innerWidth - rect.width - 8, moveEvent.clientX - offsetX)),
        top: Math.max(8, Math.min(window.innerHeight - 56, moveEvent.clientY - offsetY)),
      });
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop, { once: true });
  };

  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full border border-brand-200 bg-white px-4 py-3 text-sm font-semibold text-brand-800 shadow-xl transition hover:bg-brand-50"
        aria-label={t("analysisProgressRestore")}
      >
        {activeCount > 0 ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand-200 border-t-brand-600" />
        ) : (
          <span aria-hidden="true">✓</span>
        )}
        {t("analysisProgressTitle")}
        <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs">{activeCount || items.length}</span>
      </button>
    );
  }

  const panel = (
    <aside
      ref={panelRef}
      aria-label={t("analysisProgressTitle")}
      className="fixed z-50 flex min-h-64 min-w-80 resize overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
      style={{
        width: size.width,
        height: size.height,
        maxWidth: "calc(100vw - 16px)",
        maxHeight: "calc(100vh - 16px)",
        ...(position ?? { right: 20, bottom: 20 }),
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div
          onPointerDown={beginDrag}
          className="flex touch-none cursor-move items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3"
        >
          <div className="min-w-0">
            <h2 className="truncate text-sm font-bold text-slate-800">{t("analysisProgressTitle")}</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {activeCount > 0
                ? `${activeCount} ${t("analysisProgressActive")}`
                : `${items.length + (lessonVisible ? 1 : 0)} ${t("analysisProgressRecent")}`}
            </p>
          </div>
          <div className="ml-3 flex shrink-0 items-center gap-1">
            {items.length > 0 ? (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200"
                aria-label={t("analysisProgressExpand")}
                title={t("analysisProgressExpand")}
              >
                ⤢
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setMinimized(true);
                setExpanded(false);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200"
              aria-label={t("analysisProgressMinimize")}
              title={t("analysisProgressMinimize")}
            >
              —
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3" aria-live="polite">
          {lessonVisible && (
            <article className="rounded-lg border border-slate-200 p-3">
              <span className="mb-1 inline-block rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-700">
                {t("lessonDraftProgressBadge")}
              </span>
              <Link
                to="/educate-sage/new"
                className="mb-2 block truncate text-sm font-semibold text-slate-900 hover:text-brand-700"
              >
                {lessonJob.status === "ready" ? lessonJob.draft.title : t("newLesson")}
              </Link>
              {lessonJob.status === "drafting" ? (
                <div className="flex items-start gap-3">
                  <span
                    className="mt-0.5 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-brand-200 border-t-brand-600"
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <LessonDraftingProgress startedAt={lessonJob.startedAt} compact />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-600">
                  {lessonJob.status === "ready"
                    ? t("lessonDraftReady")
                    : lessonJob.status === "error"
                      ? lessonJob.message
                      : t("lessonDraftFailed")}
                </p>
              )}
            </article>
          )}
          {pending && (
            <article className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-start gap-3">
                <span
                  className="mt-0.5 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-brand-200 border-t-brand-600"
                  aria-hidden="true"
                />
                <PendingReviewProgress pending={pending} />
              </div>
            </article>
          )}
          {items.map((item) => (
            <article key={item.reviewId} className="rounded-lg border border-slate-200 p-2.5">
              {item.reviewType === "KNOWLEDGE_TEST" && (
                <span className="mb-1 inline-block rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-700">
                  {t("analysisProgressLessonTestBadge")}
                </span>
              )}
              <Link
                to={`/reviews/${item.reviewId}`}
                className="mb-1.5 block truncate text-sm font-semibold text-slate-900 hover:text-brand-700"
              >
                {item.title}
              </Link>
              <ReviewProgressDetail item={item} showOpenLink compact />
            </article>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-2">
          <p className="text-[11px] text-slate-400">{t("analysisProgressUpdated")}</p>
          {completedCount > 0 ? (
            <button
              type="button"
              onClick={clearCompleted}
              className="shrink-0 text-xs font-semibold text-slate-600 hover:text-brand-700 hover:underline"
            >
              {t("analysisProgressClearCompleted")}
            </button>
          ) : null}
        </div>
      </div>
    </aside>
  );

  return (
    <>
      {panel}
      <ReviewProgressDrawer open={expanded} onClose={() => setExpanded(false)} items={items} />
    </>
  );
}
